import { useLanguage } from '../../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiX, FiShoppingCart } from 'react-icons/fi';
import { toast } from 'react-toastify';
import productService from '../../services/product.service';
import { useCart } from '../../context/CartContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CompareProducts = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
    if (ids.length < 2) {
      setLoading(false);
      return;
    }

    const fetchProducts = async () => {
      try {
        const results = await Promise.all(ids.map((id) => productService.getById(id)));
        setProducts(results.map((r) => r.data.data));
      } catch (error) {
        toast.error('Failed to load products for comparison');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [searchParams]);

  const removeProduct = (id) => {
    const ids = searchParams.get('ids')?.split(',').filter((x) => x !== id) || [];
    if (ids.length < 2) {
      setSearchParams({});
      setProducts([]);
    } else {
      setSearchParams({ ids: ids.join(',') });
    }
    setProducts((prev) => prev.filter((p) => p._id !== id));
  };

  const handleAddToCart = async (productId) => {
    try {
      await addToCart(productId);
      toast.success('Added to cart!');
    } catch {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) return <LoadingSpinner />;

  if (products.length < 2) {
    return (
      <div className="section-padding text-center">
        <div className="container" style={{ maxWidth: '500px' }}>
          <h3>Compare Products</h3>
          <p className="text-muted">Select at least 2 products to compare. You can add products from the product listing page.</p>
          <Link to="/products" className="btn btn-fd-primary">{t('browseProducts')}</Link>
        </div>
      </div>
    );
  }

  // Collect all attribute keys
  const allAttributes = new Set();
  products.forEach((p) => {
    if (p.attributes) {
      const attrs = p.attributes instanceof Map ? Object.fromEntries(p.attributes) : p.attributes;
      Object.keys(attrs).forEach((k) => allAttributes.add(k));
    }
  });

  const rows = [
    { label: 'Price', render: (p) => <span className="fw-bold text-primary">${p.price?.toFixed(2)}</span> },
    { label: 'Category', render: (p) => p.category || '—' },
    { label: 'Rating', render: (p) => p.ratings?.average > 0 ? `${p.ratings.average.toFixed(1)} ⭐ (${p.ratings.count})` : '—' },
    { label: 'Stock', render: (p) => p.stock?.quantity > 0 ? `${p.stock.quantity} available` : <span className="text-danger">{t('outOfStock')}</span> },
    { label: 'Free Shipping', render: (p) => p.shipping?.freeShipping ? '✅ Yes' : '❌ No' },
    { label: 'Weight', render: (p) => p.shipping?.weight ? `${p.shipping.weight}g` : '—' },
    ...[...allAttributes].map((attr) => ({
      label: attr.charAt(0).toUpperCase() + attr.slice(1),
      render: (p) => {
        const attrs = p.attributes instanceof Map ? Object.fromEntries(p.attributes) : (p.attributes || {});
        return attrs[attr] || '—';
      },
    })),
  ];

  return (
    <div className="section-padding">
      <div className="container">
        <h2 className="mb-4">Compare Products</h2>

        <div className="table-responsive">
          <table className="table table-bordered mb-0">
            {/* Product images and names */}
            <thead>
              <tr>
                <th style={{ width: '160px', verticalAlign: 'top' }} className="bg-light">Product</th>
                {products.map((p) => {
                  const img = p.images?.find((i) => i.isPrimary) || p.images?.[0];
                  return (
                    <th key={p._id} className="text-center" style={{ minWidth: '200px', verticalAlign: 'top' }}>
                      <div className="position-relative d-inline-block">
                        <button
                          className="btn btn-sm btn-outline-danger position-absolute border-0"
                          style={{ top: '-5px', right: '-5px', zIndex: 2 }}
                          onClick={() => removeProduct(p._id)}
                          title="Remove from comparison"
                        >
                          <FiX size={14} />
                        </button>
                        <Link to={`/products/${p.slug}`}>
                          {img?.url ? (
                            <img src={img.url} alt={p.name} className="rounded mb-2"
                              style={{ width: '120px', height: '120px', objectFit: 'cover' }} />
                          ) : (
                            <div className="rounded mb-2 bg-light d-flex align-items-center justify-content-center"
                              style={{ width: '120px', height: '120px' }}>
                              <small className="text-muted">{t('noImage')}</small>
                            </div>
                          )}
                        </Link>
                      </div>
                      <div>
                        <Link to={`/products/${p.slug}`} className="text-dark text-decoration-none small fw-semibold d-block">
                          {p.name}
                        </Link>
                        <button
                          className="btn btn-sm btn-fd-primary mt-2"
                          onClick={() => handleAddToCart(p._id)}
                        >
                          <FiShoppingCart size={14} className="me-1" /> Add to Cart
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="fw-semibold bg-light small">{row.label}</td>
                  {products.map((p) => (
                    <td key={p._id} className="text-center small">{row.render(p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompareProducts;
